import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  FaUserTie, FaSearch, FaMapMarkerAlt, FaEnvelope, FaPhone, FaCalendarAlt,
  FaIdCard, FaBullhorn, FaRedo, FaWallet, FaChevronLeft, FaChevronRight
} from "react-icons/fa";

const TotalAdvertiser = () => {
  const [advertisers, setAdvertisers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 50;

  const fetchAdvertisers = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/super/admin/get/total/advertisers`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      const data = response.data.advertisers || response.data.data || response.data || [];

      if (Array.isArray(data)) {
        setAdvertisers(data);
      } else {
        setAdvertisers([]);
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Failed to fetch advertisers");
      setAdvertisers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAdvertisers();
  }, []);

  const filteredAdvertisers = advertisers.filter((adv) => {
    const term = searchTerm.toLowerCase();
    return (
      (adv.name && adv.name.toLowerCase().includes(term)) ||
      (adv.email && adv.email.toLowerCase().includes(term)) ||
      (adv.mobile && adv.mobile.toString().toLowerCase().includes(term)) ||
      (adv.city && adv.city.toLowerCase().includes(term)) ||
      (adv.state && adv.state.toLowerCase().includes(term)) ||
      (adv.iinsafId && adv.iinsafId.toLowerCase().includes(term))
    );
  }).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredAdvertisers.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredAdvertisers.length / itemsPerPage);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  return (
    <div className="min-h-screen bg-[#f8fafc] p-4 md:p-8 font-sans">
      <div className="fixed top-0 left-0 w-full h-1 bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500 z-50"></div>

      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-10">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 bg-emerald-600 rounded-2xl flex items-center justify-center text-white text-2xl shadow-xl shadow-emerald-500/20 ring-4 ring-emerald-50">
            <FaBullhorn />
          </div>
          <div>
            <h1 className="text-3xl font-black text-[#1e293b] tracking-tight">
              Advertiser Network
            </h1>
            <p className="text-[#94a3b8] text-xs font-bold tracking-tight mt-1 uppercase tracking-widest">
              {advertisers.length} verified advertising partners
            </p>
          </div>
        </div>
        <button
          onClick={fetchAdvertisers}
          className="flex items-center gap-2 px-5 py-2.5 bg-white text-[#475569] border border-[#e2e8f0] rounded-xl shadow-sm hover:bg-gray-50 hover:border-gray-300 transition-all font-semibold active:scale-95"
        >
          <FaRedo className={`${loading ? 'animate-spin' : ''}`} />
          Refresh Partners
        </button>
      </div>

      <div className="bg-white rounded-[2.5rem] shadow-2xl shadow-emerald-500/5 border border-[#f1f5f9] overflow-hidden">
        <div className="p-6 md:p-8 border-b border-[#f1f5f9] flex flex-col md:flex-row justify-between items-center gap-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-emerald-50 text-emerald-600 rounded-xl flex items-center justify-center text-lg">
              <FaSearch />
            </div>
            <div>
              <h3 className="text-xl font-bold text-[#1e293b]">Partner Registry</h3>
              <p className="text-xs text-[#94a3b8] font-bold">{filteredAdvertisers.length} partners found</p>
            </div>
          </div>
          <div className="relative w-full md:w-96 group">
            <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-[#94a3b8] group-focus-within:text-emerald-500 transition-colors" />
            <input
              type="text"
              placeholder="Search by brand name or ID..."
              className="w-full pl-12 pr-4 py-3.5 bg-[#f8fafc] border-transparent rounded-2xl outline-none focus:ring-4 focus:ring-emerald-500/10 focus:bg-white focus:border-emerald-200 transition-all text-sm font-medium text-[#334155]"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-[#f8fafc]">
                <th className="px-8 py-5 text-left text-xs font-black text-[#64748b] uppercase tracking-widest">Brand/Advertiser</th>
                <th className="px-8 py-5 text-left text-xs font-black text-[#64748b] uppercase tracking-widest">Identity</th>
                <th className="px-8 py-5 text-left text-xs font-black text-[#64748b] uppercase tracking-widest">Financials</th>
                <th className="px-8 py-5 text-left text-xs font-black text-[#64748b] uppercase tracking-widest">Contact Details</th>
                <th className="px-8 py-5 text-left text-xs font-black text-[#64748b] uppercase tracking-widest">Geography</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#f1f5f9]">
              {loading ? (
                Array(5).fill(0).map((_, i) => (
                  <tr key={i} className="animate-pulse">
                    <td colSpan="5" className="px-8 py-8"><div className="h-10 bg-gray-100 rounded-2xl w-full"></div></td>
                  </tr>
                ))
              ) : currentItems.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-8 py-20 text-center">
                    <p className="text-[#94a3b8] font-bold uppercase tracking-tight">No advertisers match your query</p>
                  </td>
                </tr>
              ) : (
                currentItems.map((adv, idx) => (
                  <tr key={adv._id} className="hover:bg-[#f8fafc] transition-all duration-200 group">
                    <td className="px-8 py-6 whitespace-nowrap">
                      <div className="flex items-center gap-4">
                        <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br from-emerald-400 to-teal-600 flex items-center justify-center text-white text-xl font-black shadow-lg shadow-emerald-500/20 group-hover:rotate-3 transition-transform`}>
                          {adv.name ? adv.name.charAt(0).toUpperCase() : <FaUserTie />}
                        </div>
                        <div>
                          <p className="text-base font-black text-[#1e293b] uppercase tracking-tight">{adv.name || "Unknown Business"}</p>
                          <p className="text-[10px] text-[#94a3b8] font-extrabold uppercase mt-0.5 tracking-wider">Partner since {adv.createdAt ? new Date(adv.createdAt).toLocaleDateString() : 'N/A'}</p>
                        </div>
                      </div>
                    </td>

                    <td className="px-8 py-6 whitespace-nowrap">
                      <div className="inline-flex flex-col gap-1.5">
                        <span className="px-3 py-1 bg-emerald-50 text-emerald-600 rounded-lg text-[10px] font-black uppercase tracking-widest border border-emerald-100">
                          PRO_PARTNER
                        </span>
                        {adv.iinsafId && (
                          <div className="flex items-center gap-1 px-2.5 py-1 bg-gray-100 rounded-lg text-gray-800 text-[11px] font-mono font-black border border-gray-200">
                            {adv.iinsafId}
                          </div>
                        )}
                      </div>
                    </td>

                    <td className="px-8 py-6 whitespace-nowrap">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-emerald-50 text-emerald-600 rounded-xl flex items-center justify-center text-sm">
                          <FaWallet />
                        </div>
                        <div>
                          <p className="text-xs text-[#94a3b8] font-bold uppercase mb-0.5">Ad Funds</p>
                          <p className="text-lg font-black text-[#1e293b]">₹{(adv.walletBalance || 0).toFixed(2)}</p>
                        </div>
                      </div>
                    </td>

                    <td className="px-8 py-6 whitespace-nowrap">
                      <div className="flex flex-col gap-2">
                        <div className="flex items-center gap-2">
                          <FaEnvelope className="text-gray-300 text-[10px]" />
                          <span className="text-sm font-bold text-[#475569]">{adv.email || 'N/A'}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <FaPhone className="text-gray-300 text-[10px]" />
                          <span className="text-sm font-bold text-[#475569]">{adv.mobile || 'N/A'}</span>
                        </div>
                      </div>
                    </td>

                    <td className="px-8 py-6 whitespace-nowrap">
                      <div className="flex flex-col">
                        <span className="text-sm font-black text-[#1e293b] uppercase tracking-tighter">{adv.city || "National"}</span>
                        <span className="text-[10px] font-bold text-[#94a3b8] uppercase tracking-widest">{adv.state || "India"}</span>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="p-8 bg-[#f8fafc] border-t border-[#f1f5f9] flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-sm text-[#64748b] font-bold">
              Page <span className="text-[#1e293b] font-black">{currentPage}</span> of <span className="text-[#1e293b] font-black">{totalPages}</span>
            </p>
            <div className="flex items-center gap-2 bg-white p-1.5 rounded-2xl border border-[#e2e8f0] shadow-sm">
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="w-10 h-10 flex items-center justify-center text-[#64748b] hover:bg-emerald-50 hover:text-emerald-600 disabled:opacity-30 transition-all rounded-xl"
              >
                <FaChevronLeft size={12} />
              </button>
              <button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="w-10 h-10 flex items-center justify-center text-[#64748b] hover:bg-emerald-50 hover:text-emerald-600 disabled:opacity-30 transition-all rounded-xl"
              >
                <FaChevronRight size={12} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TotalAdvertiser;